import { useEffect, useLayoutEffect, useState, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { db, auth, functions } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { MembershipContext } from '../contexts/MembershipContext';
import { useContact } from '../contexts/ContactContext';
import { getNetCashReceived, userNeedsPayoutOnboarding } from '../utils/tradeUtils';
import { openCheckout } from '../services/nativeBrowser';
import { UserContext } from '../contexts/UserContext';
import AddressRequiredModal from '../components/AddressRequiredModal';
import PayoutAccountLinkedModal from '../components/PayoutAccountLinkedModal';

const STRIPE_FN_BASE = 'https://us-central1-pedalxchange.cloudfunctions.net';

const SellerPurchaseSettlement = () => {
  const { offerId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getTradeCompletionFee, getTradeProtectFee } = useContext(MembershipContext);
  const { openContact } = useContact();
  const { firestoreUser, loading: userDataLoading } = useContext(UserContext);
  const [offerData, setOfferData] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  // { reconnected } — informational alreadyOnboarded notice (no navigation).
  const [payoutLinkedNotice, setPayoutLinkedNotice] = useState(null);

  // The seller's address is required for the Shippo label, but this page had
  // no prompt at all. Auto-open the same inline modal the offer pages use so
  // the seller can fill it in place (found during Android testing, 2026-08-20).
  const isAddressComplete = (address) => {
    if (!address) return false;
    const required = ['name', 'street1', 'city', 'state', 'zip', 'country'];
    return required.every(field => address[field] && address[field].trim());
  };
  useEffect(() => {
    if (!userDataLoading && !isAddressComplete(firestoreUser?.shippingAddress)) {
      setShowAddressModal(true);
    } else {
      setShowAddressModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firestoreUser, userDataLoading]);
  const [targetPedal, setTargetPedal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [buyerUsername, setBuyerUsername] = useState('');
  // Referral credit on the seller's account — waives the listing fee (mirrors the
  // SellerPurchaseConfirm display so fees shown here match what they saw there).
  const [referralCredits, setReferralCredits] = useState(0);
  // null = still determining; true/false = Stripe payout onboarding complete or not.
  // Gates settling for a net cash-receiver so they can't proceed without a usable
  // payout account (e.g. after dismissing the onboarding sheet without finishing).
  const [payoutReady, setPayoutReady] = useState(null);
  const [settingUpPayout, setSettingUpPayout] = useState(false);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [offerId]);

  useEffect(() => {
    if (!loading) {
      window.scrollTo(0, 0);
    }
  }, [loading, offerId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Load purchase offer data
        const offerRef = doc(db, 'purchaseOffers', offerId);
        const offerSnap = await getDoc(offerRef);
        
        if (!offerSnap.exists()) {
          // Auth is already resolved here (fetchData only runs for a logged-in
          // user), so a missing doc is a genuine not-found — never a login flash.
          setMessage('Purchase offer not found.');
          setLoading(false);
          return;
        }

        const data = offerSnap.data();
        setOfferData({ id: offerSnap.id, ...data });

        // Load target pedal
        const targetPedalSnap = await getDoc(doc(db, 'listings', data.targetPedalId));
        if (!targetPedalSnap.exists()) {
          setMessage('Target pedal not found.');
          setLoading(false);
          return;
        }

        const targetPedalData = { id: targetPedalSnap.id, ...targetPedalSnap.data() };
        setTargetPedal(targetPedalData);

        // Fetch buyer username
        const buyerRef = doc(db, 'publicProfiles', data.initiatorId);
        const buyerSnap = await getDoc(buyerRef);
        const buyerUsername = buyerSnap.exists() ? buyerSnap.data().username || 'Buyer' : 'Buyer';
        setBuyerUsername(buyerUsername);

      } catch (err) {
        console.error('Error fetching purchase data:', err);
        setMessage('Error loading purchase details');
      } finally {
        setLoading(false);
      }
    };

    // Wait for auth to resolve before deciding. On a fresh load / pull-to-refresh
    // reload, auth.currentUser is briefly null; driving off onAuthStateChanged
    // keeps a logged-in user on the loading state instead of flashing a login /
    // not-found message.
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        sessionStorage.setItem('returnAfterLogin', window.location.pathname);
        setMessage('Please log in to view this purchase. Click here to log in: /login');
        setLoading(false);
        return;
      }
      fetchData();
    });

    return () => unsubscribe();
  }, [offerId]);

  // Reconcile payout-onboarding status with Stripe, then read the authoritative
  // flag back from Firestore. `checkStripeAccountStatus` only sets
  // stripeOnboardingComplete when the account is truly ready (details submitted +
  // transfers active), so dismissing the sheet early can never mark it ready.
  // Returns the resolved completion boolean. Mirrors TradeSettlement.
  const recheckPayoutStatus = async (stripeAccountIdOverride) => {
    const user = auth.currentUser;
    if (!user) return false;
    try {
      const userRef = doc(db, 'users', user.uid);
      const snap = await getDoc(userRef);
      let stripeAccountId = snap.exists() ? snap.data().stripeAccountId : null;
      if (stripeAccountIdOverride) stripeAccountId = stripeAccountIdOverride;

      await fetch(`${STRIPE_FN_BASE}/checkStripeAccountStatus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({ userId: user.uid, stripeAccountId: stripeAccountId || null })
      });

      const fresh = await getDoc(userRef);
      const complete = fresh.exists() ? !!fresh.data().stripeOnboardingComplete : false;
      setPayoutReady(complete);
      return complete;
    } catch (err) {
      console.error('Error rechecking payout status:', err);
      return false;
    }
  };

  // Baseline read on mount (covers direct visits / prior-session onboarding) so we
  // know whether to gate before the user can tap settle. Also loads referralCredits
  // for the fee-waiver display.
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (cancelled) return;
        const complete = snap.exists() ? !!snap.data().stripeOnboardingComplete : false;
        // Race guard: this baseline read and the onboarding-return recheck both run
        // on mount. The recheck hits live Stripe and is authoritative, while this
        // read can resolve late with a pre-webhook stale doc — so never downgrade a
        // confirmed-true here, only fill in when still undetermined/false.
        setPayoutReady(prev => (prev === true ? true : complete));
        setReferralCredits(snap.exists() ? (snap.data().referralCredits || 0) : 0);
      } catch {
        if (!cancelled) setPayoutReady(prev => (prev === true ? true : false));
      }
    })();
    return () => { cancelled = true; };
  }, [auth.currentUser]);

  // On return from onboarding, reconcile against Stripe (handles the native handoff
  // where the account id rides in on the URL), then clean up the URL params.
  useEffect(() => {
    if (searchParams.get('onboarding') !== 'return') return;
    const urlStripeAccountId = new URLSearchParams(window.location.search).get('stripeAccountId');
    (async () => {
      await recheckPayoutStatus(urlStripeAccountId || undefined);
      window.history.replaceState({}, document.title, window.location.pathname);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, auth.currentUser]);

  // Start (or resume) Stripe Connect payout onboarding for a net cash-receiver who
  // hasn't finished it. Native opens the in-app sheet and re-verifies on return (the
  // sheet's X must NOT mark payouts ready); web does a full-page redirect and Stripe's
  // return_url brings them back with ?onboarding=return. Mirrors TradeSettlement.
  const handleSetupPayout = async () => {
    setSettingUpPayout(true);
    setMessage('');
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Authentication required');
      const idToken = await user.getIdToken();
      const returnUrl = `${window.location.origin}/seller-purchase-settlement/${offerId}?onboarding=return`;

      const resp = await fetch(`${STRIPE_FN_BASE}/createStripeConnectAccount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({
          userId: user.uid,
          returnUrl,
          returnTo: Capacitor.isNativePlatform() ? 'app' : undefined
        })
      });
      const data = await resp.json();

      if (data.alreadyOnboarded) {
        setPayoutReady(true);
        // Tell the user why Stripe onboarding never appeared (especially the
        // email-reconnect case) instead of silently flipping the gate.
        setPayoutLinkedNotice({ reconnected: !!data.reconnected });
        return;
      }
      const onboardingUrl = data.onboardingUrl || data.accountLinkUrl;
      if (!onboardingUrl) throw new Error('Could not start payout setup. Please try again.');

      const acctId = data.stripeAccountId;
      await openCheckout(onboardingUrl, {
        onReturn: async () => {
          // POLL, don't check once: Stripe can flip `transfers` to active a few
          // seconds after onboarding finishes; a single immediate check would tell
          // a successfully-onboarded seller their setup "wasn't finished".
          let complete = false;
          for (let attempt = 0; attempt < 6 && !complete; attempt++) {
            if (attempt > 0) await new Promise(r => setTimeout(r, 2500));
            complete = await recheckPayoutStatus(acctId);
          }
          if (!complete) {
            setMessage('Payout setup wasn’t finished — tap “Set up payout” to complete it before settling.');
          }
        }
      });
    } catch (err) {
      console.error('Error starting payout setup:', err);
      setMessage(`Error: ${err.message}`);
    } finally {
      setSettingUpPayout(false);
    }
  };

  const handleSettlePurchase = async () => {
    setProcessing(true);
    setMessage('');

    try {
      const userId = auth.currentUser?.uid;
      if (!userId) throw new Error('Authentication required');

      const isSeller = userId === offerData.responderId;
      
      if (!isSeller) {
        throw new Error('Only the seller can complete this action');
      }

      // Gate (defense-in-depth; the UI already swaps the button when setup is
      // needed): a NET cash-receiver must have a completed Stripe payout account
      // before settling. Uses the same shared net value as the render gate.
      if (userNeedsPayoutOnboarding({ netCashReceived, stripeOnboardingComplete: payoutReady === true })) {
        setMessage('Please set up your payout account before settling so you can receive your funds.');
        return; // finally resets `processing`
      }

      // Settle SERVER-SIDE via the settlePurchase callable. Besides marking
      // the seller paid (what the old client updateDoc did), the server
      // computes the seller's NET payout (protection choice, card-paid fees,
      // referral credit, membership tier) and persists it as payoutAdjustment
      // — the amount the escrow release actually transfers. Client-side math
      // here is display only and must never be the source of truth for money.
      console.log('🔄 Settling purchase server-side:', { offerId: offerData.id, userId });
      const settlePurchaseFn = httpsCallable(functions, 'settlePurchase');
      const settleResult = await settlePurchaseFn({ purchaseId: offerData.id });
      console.log('✅ Purchase settled:', settleResult.data);
      
      // Navigate to purchase progress page
      navigate(`/purchase-progress/${offerData.id}`);
      
    } catch (err) {
      console.error('❌ Error settling purchase:', err);
      setMessage(`Error: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' }}>
        <div className="text-center">Loading purchase settlement...</div>
      </div>
    );
  }

  if (message && !offerData) {
    return (
      <div className="p-4" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' }}>
        <div className="text-center text-red-600">{message}</div>
        <div className="text-center mt-4">
          <button 
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:underline"
          >
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  const purchaseAmount = Number(offerData.acceptedAmount || offerData.offerAmount) || 0;
  let listingFee = getTradeCompletionFee();
  // Referral credit waives the listing fee — match the SellerPurchaseConfirm display
  // so the seller doesn't see different fee numbers between confirm and settlement.
  const hasReferralCredit = listingFee > 0 && referralCredits > 0;
  if (hasReferralCredit) listingFee = 0;
  const protectionFee = offerData.protection?.seller ? getTradeProtectFee() : 0;
  const totalFees = listingFee + protectionFee;
  const cashReceived = purchaseAmount;
  const appliedToFees = Math.min(cashReceived, totalFees);
  const remainingCash = cashReceived - appliedToFees;

  // NET payout the seller actually pockets — drives the payout gate (mirrors
  // TradeSettlement). Settlement always applies received cash to fees (no toggle).
  const netCashReceived = getNetCashReceived({ grossReceived: cashReceived, totalFees, applyCashCredit: true });
  const requiresPayout = netCashReceived > 0;
  // payoutReady: null = still checking.
  const needsPayoutSetup = requiresPayout && payoutReady === false;
  const payoutChecking = requiresPayout && payoutReady === null;

  return (
    <div className="trade-settlement-outer" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column', padding: '1.5rem', boxSizing: 'border-box', overflowX: 'hidden' }}>
      <style>{`
        @media (max-width: 768px) {
          .trade-settlement-outer {
            padding: 0.75rem !important;
          }
          .trade-settlement-card {
            padding: 1rem !important;
            border-radius: 8px !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            max-width: 100% !important;
          }
          .trade-settlement-outer h2 {
            font-size: 1.5rem !important;
            margin-bottom: 1rem !important;
            padding: 0 0.25rem !important;
          }
          .trade-settlement-footer {
            padding-bottom: 80px !important;
          }
        }
      `}</style>

      <h2 className="text-2xl font-bold mb-4 text-center" style={{ fontFamily: 'Nunito, sans-serif' }}>
        Settle Sale
      </h2>

      {/* Payout Information */}
      {cashReceived > 0 && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg" style={{ maxWidth: '700px', margin: '0 auto 16px auto', padding: '16px 20px', width: '100%', boxSizing: 'border-box' }}>
          <p className="text-blue-800 text-sm" style={{ margin: 0, lineHeight: '1.5' }}>
            <strong>Automatic Payout:</strong> Once this sale is complete and funds are released, your balance will automatically transfer to your connected bank account within 2 business days.
          </p>
        </div>
      )}

      <div className="trade-settlement-card" style={{ background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '1.25rem', fontFamily: 'Nunito, sans-serif', maxWidth: '700px', margin: '0 auto 24px auto', width: '100%', boxSizing: 'border-box' }}>
        <h4 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}>Payment breakdown</h4>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
          <span style={{ color: '#374151' }}>Selling to</span>
          <span style={{ fontWeight: '600', color: '#374151' }}>{buyerUsername}</span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
          <span style={{ color: '#374151' }}>Pedal</span>
          <span style={{ fontWeight: '600', color: '#374151' }}>{targetPedal.make} {targetPedal.model}</span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
          <span style={{ color: '#374151' }}>Cash you're receiving</span>
          <span style={{ fontWeight: '600', color: '#15803d' }}>${cashReceived.toFixed(2)}</span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
          <span style={{ color: '#374151' }}>Total fees</span>
          <span style={{ fontWeight: '600', color: '#374151' }}>${totalFees.toFixed(2)}</span>
        </div>

        {totalFees > 0 && protectionFee > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.8rem', paddingLeft: '0.75rem' }}>
            <span style={{ color: '#6b7280' }}>Includes TradeProtect</span>
            <span style={{ fontWeight: '600', color: '#6b7280' }}>${protectionFee.toFixed(2)}</span>
          </div>
        )}

        <div style={{ borderTop: '2px solid #d1d5db', marginTop: '0.75rem', paddingTop: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: '700', fontSize: '1rem', color: '#1f2937' }}>You'll receive</span>
          <span style={{ fontWeight: '700', fontSize: '1.1rem', color: '#15803d' }}>
            ${remainingCash.toFixed(2)}
          </span>
        </div>

        {cashReceived > 0 && remainingCash >= 0 && (
          <div style={{ background: '#e8f5f0', border: '1px solid #b2dfdb', borderRadius: '8px', padding: '0.75rem', marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#4db6ac', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span style={{ fontSize: '0.85rem', color: '#2e7d6e' }}>
              {remainingCash > 0
                ? `Nothing to pay — you'll receive $${remainingCash.toFixed(2)} once the sale is complete.`
                : "Nothing to pay — your fees are fully covered by the sale amount."}
            </span>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
          {needsPayoutSetup ? (
            <div style={{ width: '100%', maxWidth: '360px' }}>
              <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '10px', padding: '14px 16px', marginBottom: '12px', textAlign: 'left' }}>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#9a3412', lineHeight: '1.5' }}>
                  <strong>You're receiving ${cashReceived.toFixed(2)} from this sale.</strong> To get paid, you'll set up a one-time, free, secure payout account with Stripe.
                </p>
              </div>
              <button
                onClick={handleSetupPayout}
                disabled={settingUpPayout}
                style={{
                  backgroundColor: settingUpPayout ? '#9CA3AF' : '#C9A227',
                  color: 'white',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '12px 24px',
                  fontFamily: 'Nunito, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  cursor: settingUpPayout ? 'not-allowed' : 'pointer',
                  width: '100%',
                  opacity: settingUpPayout ? 0.7 : 1
                }}
              >
                {settingUpPayout ? 'Opening secure setup…' : `Set up payout to receive $${cashReceived.toFixed(2)}`}
              </button>
            </div>
          ) : payoutChecking ? (
            <button
              disabled
              style={{
                backgroundColor: '#9CA3AF', color: 'white', border: 'none', borderRadius: '9999px',
                padding: '12px 24px', fontFamily: 'Nunito, sans-serif', fontWeight: 'bold', fontSize: '16px',
                cursor: 'not-allowed', maxWidth: '300px', width: '100%', opacity: 0.7
              }}
            >
              Checking payout setup…
            </button>
          ) : (
            <button
              onClick={handleSettlePurchase}
              disabled={processing}
              style={{
                backgroundColor: processing ? '#9CA3AF' : '#C9A227',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',
                padding: '12px 24px',
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 'bold',
                fontSize: '16px',
                cursor: processing ? 'not-allowed' : 'pointer',
                maxWidth: '300px',
                width: '100%',
                opacity: processing ? 0.7 : 1
              }}
            >
              {processing ? 'Processing...' : 'Settle Sale & Continue'}
            </button>
          )}
        </div>
      </div>

      {message && (
        <div className={`mt-4 p-3 rounded ${
          message.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
        }`}>
          {message}
        </div>
      )}

      {/* Fixed gap below content */}
      <div style={{ height: '60px' }}></div>
      
      {/* Footer */}
      <div className="py-4 trade-settlement-footer" style={{ paddingBottom: '10px', backgroundColor: '#F7F3EB' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Need help or found a bug?{' '}
              <button 
                onClick={() => openContact({ category: 'support' })}
                className="hover:underline"
                style={{ 
                  color: '#5A9ECB',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer'
                }}
              >
                Contact PXC Support
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Inline shipping-address modal — auto-opens when the seller has no
          complete address on file (needed for the Shippo label). */}
      <AddressRequiredModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onSaved={() => setShowAddressModal(false)}
        user={firestoreUser}
        context="shipping"
      />

      <PayoutAccountLinkedModal
        isOpen={!!payoutLinkedNotice}
        reconnected={payoutLinkedNotice?.reconnected}
        onClose={() => setPayoutLinkedNotice(null)}
      />
    </div>
  );
};

export default SellerPurchaseSettlement;
